import { Refine, Authenticated } from "@refinedev/core";
import { useNotificationProvider, ThemedLayout } from "@refinedev/antd";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import "@refinedev/antd/dist/reset.css";
import "./styles/global.css";
import * as Sentry from "@sentry/react";

import routerProvider, {
  DocumentTitleHandler,
  UnsavedChangesNotifier,
  CatchAllNavigate,
} from "@refinedev/react-router";
import { App as AntdApp, ConfigProvider } from "antd";
import enUS from "antd/locale/en_US";
import esES from "antd/locale/es_ES";
import { BrowserRouter, Route, Routes, Outlet, Navigate } from "react-router";
import { 
  HomeOutlined,
  CalendarOutlined,
  AuditOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  WarningOutlined,
  BarChartOutlined,
  UserOutlined,
  UserAddOutlined,
  SettingOutlined,
  ExperimentOutlined,
  TeamOutlined,
  ApartmentOutlined,
  CustomerServiceOutlined,
  TagsOutlined,
  IdcardOutlined,
  SolutionOutlined,
  MergeCellsOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { authProvider } from "./providers/authProvider";
import { dataProvider } from "./providers/dataProvider";
import { i18nProvider } from "./providers/i18nProvider";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { LoadingSpinner, CustomHeader, CustomSider, AdminRoute, CyberAdminRoute, AuditorRoute, CoordinatorRoute, RoleBasedHomeRedirect, TicketRedirect, PlanningCommentRedirect } from "./components";
import { useChunkErrorNotifier } from "./hooks";

/**
 * `ChunkErrorGuard` — hijo directo de `<AntdApp>` que engancha el
 * hook `useChunkErrorNotifier`. Necesita vivir DENTRO de <AntdApp>
 * porque el hook usa `App.useApp()` para obtener el `notification`
 * ligado al ConfigProvider (theme, locale). No renderiza nada.
 */
const ChunkErrorGuard = () => {
  useChunkErrorNotifier();
  return null;
};
import { Login } from "./pages/login";
import { Home } from "./pages/home";
import {
  PlanningGroupList,
  PlanningGroupCreate,
  PlanningGroupEdit,
  PlanningGroupShow,
  PlanningAssignAudits,
} from "./pages/planning-groups";
import { PlanningGroupMap } from "./pages/planning-groups/map";
import TestCustomMap from "./pages/planning-groups/test-custom-map";
import { AuditList, AuditShow, AuditMap, AuditPlotForm, AuditPlotReview, AuditEditInfo, PlotGeometryMap } from "./pages/audits";
import { ProducerContactsView } from "./pages/contacts/show";
import { ControlPointList, ControlPointShow, ControlPointCreate, ControlPointEdit, ControlPointEditFull } from "./pages/control-points";
import { AdjacentCropList, AdjacentCropShow, AdjacentCropMap, AdjacentCropCreate, AdjacentCropEdit } from "./pages/adjacent-crops";
import { IncidenceList, IncidenceCreate, IncidenceShow, IncidenceEdit, RegularizedSurfaceReport } from "./pages/incidences";
import { FarmProductionList, FarmProductionImport } from "./pages/farm-production";
import {
  ProducerList,
  ProducerCreate,
  ProducerEdit,
  ProducerShow,
  FarmCreate,
  FarmEdit,
} from "./pages/producers";
import { FarmList, FarmShow, FarmMap, PlotRealEdit } from "./pages/farms";
import { SampleAdminList } from "./pages/admin/samples";
import { Dashboard } from "./pages/dashboard";
import {
  AuditorHome,
  AuditorPlanningGroupsList,
  AuditorProfile,
  AuditorChangePassword,
  AuditorSamplesPending,
  CoordinatorAssign,
  CoordinatorAuditors,
} from "./pages/auditor";
import { TicketsList, TicketShow } from "./pages/tickets";
import {
  AuditCompanyAdminList,
  AuditCompanyAdminShow,
} from "./pages/admin/audit-companies";
import { AuditorAssignmentAdminList } from "./pages/admin/auditor-assignments";
import { AdminUsersList } from "./pages/admin/users";
import { TicketCategoryAdminList } from "./pages/admin/ticket-categories";
import { BusinessPositionsList } from "./pages/admin/business-positions";
import { AdminContactDuplicatesView } from "./pages/admin/contacts";
import { PatternsList } from "./pages/admin/patterns";
import { DecreaseReasonsList } from "./pages/admin/decrease-reasons";
import { SuspiciousCropReasonsList } from "./pages/admin/suspicious-crop-reasons";
import { VarietiesList } from "./pages/admin/varieties";
import { VarietyConfirmationsList } from "./pages/admin/variety-confirmations";
import { VarietyConfirmationsMenuBadge } from "./pages/admin/variety-confirmations/MenuBadge";
import { CropsList, CropsLegacyMigrationList } from "./pages/admin/crops";
import { ContactProposalsList } from "./pages/admin/contact-proposals";
import { SeasonsAdminList } from "./pages/admin/seasons";
import { SproutIcon } from "./components/common/SproutIcon";
import { TreeIcon } from "./components/common/TreeIcon";
import { ContactImportPage } from "./pages/admin/contact-import";
import {
  OnboardingLayout,
  OnboardingHome,
  ChapterPage as OnboardingChapterPage,
  DocumentsPage as OnboardingDocumentsPage,
  VideosPage as OnboardingVideosPage,
  FAQPage as OnboardingFAQPage,
} from "./pages/onboarding";
import { OnboardingAssetsAdminList } from "./pages/admin/onboarding-assets";
import { API_BASE_URL } from "./utils/constants";
import "./i18n/config";

/**
 * Loading component displayed during authentication check
 */
const LoadingComponent = () => (
  <LoadingSpinner fullScreen tip="Checking authentication..." />
);

/**
 * Sentry test button — only rendered when VITE_SENTRY_DSN is set.
 * Visit /sentry-debug and click the button to trigger a test error.
 */
const SentryDebugPage = () => {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    return <p style={{ padding: 24 }}>Sentry not configured (VITE_SENTRY_DSN not set).</p>;
  }
  return (
    <div style={{ padding: 24 }}>
      <h2>Sentry Debug</h2>
      <p>Click the button to throw a test error and verify Sentry is capturing events.</p>
      <button
        onClick={() => {
          throw new Error("This is your first error!");
        }}
        style={{ padding: "8px 16px", cursor: "pointer" }}
      >
        Break the world
      </button>
    </div>
  );
};

/**
 * Main App Component
 * Configures Refine with authentication, routing, and data providers
 */
function App() {
  const { t, i18n } = useTranslation();
  
  // Get Ant Design locale based on current language
  const antdLocale = i18n.language === "es" ? esES : enUS;
  
  return (
    <BrowserRouter>
      <Sentry.ErrorBoundary fallback={<p>Ha ocurrido un error inesperado.</p>}>
      <ColorModeContextProvider>
        <ConfigProvider 
          locale={antdLocale}
          wave={{ disabled: true }}
        >
          <AntdApp>
            <ChunkErrorGuard />
            <RefineKbarProvider>
              <Refine
            dataProvider={dataProvider(API_BASE_URL)}
            notificationProvider={useNotificationProvider}
            routerProvider={routerProvider}
            authProvider={authProvider}
            i18nProvider={i18nProvider}
            resources={[
              {
                name: "home",
                list: "/",
                meta: {
                  label: t("home.title"),
                  icon: <HomeOutlined />,
                },
              },
              {
                name: "planning-groups",
                list: "/planning-groups",
                create: "/planning-groups/create",
                edit: "/planning-groups/edit/:id",
                show: "/planning-groups/show/:id",
                meta: {
                  label: t("planning.title"),
                  icon: <CalendarOutlined />,
                },
              },
              {
                name: "audits",
                list: "/audits",
                show: "/audits/:id",
                meta: {
                  label: t("audit.title"),
                  icon: <AuditOutlined />,
                },
              },
              {
                name: "control-points",
                list: "/control-points",
                show: "/control-points/:id",
                edit: "/control-points/edit/:id",
                meta: {
                  label: t("controlPoint.title"),
                  icon: <EnvironmentOutlined />,
                  // Routes stay mounted (deep links keep working) but
                  // the sidebar entry is hidden.
                  hide: true,
                },
              },
              {
                name: "adjacent-crops",
                list: "/adjacent-crops",
                show: "/adjacent-crops/:id",
                edit: "/adjacent-crops/edit/:id",
                meta: {
                  label: t("adjacent_crops.title", "Cultivos Adyacentes"),
                  icon: <AppstoreOutlined />,
                  hide: true,
                },
              },
              // Top-level admin shortcuts — promoted out of the
              // "Admin" submenu so the most-used pages are one click
              // away. Visibility is still admin-only via
              // ADMIN_ONLY_RESOURCES in CustomSider.
              {
                name: "v1/producers",
                identifier: "producers",
                list: "/producers",
                create: "/producers/create",
                edit: "/producers/edit/:id",
                show: "/producers/show/:id",
                meta: {
                  label: t("producers.title"),
                  icon: <UserOutlined />,
                },
              },
              {
                name: "v1/farms",
                identifier: "farms",
                list: "/farms",
                show: "/farms/show/:id",
                meta: {
                  label: t("farms.title", "Fincas"),
                  icon: <EnvironmentOutlined />,
                },
              },
              {
                name: "farm-production-records",
                list: "/farm-production",
                create: "/farm-production/import",
                meta: {
                  label: "Producción",
                  icon: <BarChartOutlined />,
                },
              },
              {
                name: "v1/samples",
                identifier: "samples",
                list: "/admin/samples",
                meta: {
                  label: t("samples.admin_title", "Muestras Lab."),
                  icon: <ExperimentOutlined />,
                },
              },
              {
                name: "v1/tickets",
                identifier: "admin-tickets",
                list: "/admin/tickets",
                show: "/admin/tickets/:id",
                meta: {
                  // Single unified "Incidencias" entry — covers both
                  // audit incidences and support tickets. The list
                  // has a "Origen" filter to slice between the two.
                  label: t("incidence.title", "Incidencias"),
                  icon: <WarningOutlined />,
                },
              },
              {
                name: "admin",
                meta: {
                  label: "Admin",
                  icon: <SettingOutlined />,
                },
              },
              // Legacy `incidences` resource — routes still mounted
              // (deep links to /incidences/:id keep working) but the
              // sidebar entry has been folded into the unified
              // Tickets bandeja. Hidden via meta.hide = true.
              {
                name: "incidences",
                list: "/incidences",
                show: "/incidences/:id",
                edit: "/incidences/edit/:id",
                create: "/incidences/create",
                meta: {
                  label: t("incidence.title", "Incidencias"),
                  icon: <WarningOutlined />,
                  parent: "admin",
                  hide: true,
                },
              },
              {
                name: "v1/admin/audit-companies",
                identifier: "audit-companies",
                list: "/admin/audit-companies",
                show: "/admin/audit-companies/:id",
                meta: {
                  label: t("admin_audit_companies.title", "Empresas auditoras"),
                  icon: <ApartmentOutlined />,
                  parent: "admin",
                },
              },
              {
                name: "v1/admin/auditor-assignments",
                identifier: "auditor-assignments",
                list: "/admin/auditor-assignments",
                meta: {
                  label: t("admin_auditor_assignments.title", "Asignaciones de auditores"),
                  icon: <TeamOutlined />,
                  parent: "admin",
                },
              },
              {
                name: "v1/admin/users",
                identifier: "admin-users",
                list: "/admin/users",
                meta: {
                  label: t("admin_users.title", "Gestión de usuarios"),
                  icon: <UserAddOutlined />,
                  parent: "admin",
                },
              },
              {
                name: "v1/admin/ticket-categories",
                identifier: "ticket-categories",
                list: "/admin/ticket-categories",
                meta: {
                  label: t("ticket_categories.title", "Categorías de incidencias"),
                  icon: <TagsOutlined />,
                  parent: "admin",
                },
              },
              {
                name: "business-positions",
                identifier: "business-positions",
                list: "/admin/business-positions",
                meta: {
                  label: t("business_positions.title", "Cargos de contacto"),
                  // IdcardOutlined: semánticamente "cargo / posición",
                  // distinto del TagsOutlined que usan las
                  // Categorías de incidencias.
                  icon: <IdcardOutlined />,
                  parent: "admin",
                },
              },
              {
                name: "admin/contacts/duplicates",
                identifier: "admin-contact-duplicates",
                list: "/admin/contacts/duplicates",
                meta: {
                  label: t(
                    "admin.contacts.duplicates.menu_label",
                    "Fusionar contactos duplicados",
                  ),
                  icon: <MergeCellsOutlined />,
                  parent: "admin",
                },
              },
              {
                name: "admin/patterns",
                identifier: "admin-patterns",
                list: "/admin/patterns",
                meta: {
                  label: t("patterns.title", "Patrones de parcelas"),
                  icon: <SproutIcon />,
                  parent: "admin",
                },
              },
              {
                name: "admin/decrease-reasons",
                identifier: "admin-decrease-reasons",
                list: "/admin/decrease-reasons",
                meta: {
                  label: t("decrease_reasons.title", "Motivos de merma"),
                  icon: <WarningOutlined />,
                  parent: "admin",
                },
              },
              {
                name: "admin/suspicious-crop-reasons",
                identifier: "admin-suspicious-crop-reasons",
                list: "/admin/suspicious-crop-reasons",
                meta: {
                  label: t(
                    "suspicious_crop_reasons.title",
                    "Motivos de punto de control",
                  ),
                  icon: <EnvironmentOutlined />,
                  parent: "admin",
                },
              },
              {
                name: "admin/varieties",
                identifier: "admin-varieties",
                list: "/admin/varieties",
                meta: {
                  label: t("varieties.title", "Variedades Protegidas"),
                  icon: <SproutIcon />,
                  parent: "admin",
                },
              },
              {
                name: "admin/variety-confirmations",
                identifier: "admin-variety-confirmations",
                list: "/admin/variety-confirmations",
                meta: {
                  label: t(
                    "variety_confirmations.title",
                    "Confirmar variedad",
                  ),
                  // PR4 — Icon dinámico con badge: muestra el número
                  // de divergencias pendientes (poll cada 60 s).
                  icon: <VarietyConfirmationsMenuBadge />,
                  parent: "admin",
                },
              },
              {
                name: "admin/crops",
                identifier: "admin-crops",
                list: "/admin/crops",
                meta: {
                  label: t("crops.title", "Especies y variedades"),
                  icon: <TreeIcon />,
                  parent: "admin",
                },
              },
              // "Validación de contactos" ya no aparece en el sidebar
              // porque la acción vive inline en el detalle del ticket
              // (/admin/tickets/<id> → botón "Aplicar propuesta de
              // contacto" abre el modal sin viajar a otra página). La
              // ruta sigue montada abajo para no romper bookmarks o
              // deep-links viejos (?ticket=…) y por si alguien
              // necesita el override de `position_id` del catálogo.
              {
                name: "v1/admin/seasons",
                identifier: "admin-seasons",
                list: "/admin/seasons",
                meta: {
                  label: t("seasons.title", "Campañas"),
                  icon: <CalendarOutlined />,
                  parent: "admin",
                },
              },
              // Onboarding — manual del puesto. Sección top-level
              // admin-only (filtrada en `CustomSider` vía
              // `ADMIN_ONLY_RESOURCES`). El sub-menú lateral con los
              // capítulos vive dentro de la propia sección
              // (`OnboardingLayout`).
              {
                name: "onboarding",
                list: "/onboarding",
                meta: {
                  label: t("onboarding.title", "Onboarding"),
                  icon: <BookOutlined />,
                },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              disableTelemetry: true,
            }}
          >
            <Routes>
              {/* Protected Routes - Require Authentication */}
              <Route
                element={
                  <Authenticated
                    key="authenticated-routes"
                    fallback={<CatchAllNavigate to="/login" />}
                    loading={<LoadingComponent />}
                  >
                    <ThemedLayout Header={CustomHeader} Sider={CustomSider}>
                      <Outlet />
                    </ThemedLayout>
                  </Authenticated>
                }
              >
                {/* Home/Dashboard Route — auditors get redirected to /auditor/home */}
                <Route index element={<RoleBasedHomeRedirect fallback={<Home />} />} />
                <Route path="/" element={<RoleBasedHomeRedirect fallback={<Home />} />} />

                {/* Per-role dashboard (coordinator + auditor). Lives
                    at /dashboard so the menu can deep-link to it and
                    the AuditorHome page can `<Navigate>` here. The
                    component decides what to render based on the
                    response from /v1/dashboard/. */}
                <Route path="/dashboard" element={<Dashboard />} />

                {/* Neutral landing for ticket email links: redirects to
                    /admin/tickets/:id or /auditor/incidences/:id based
                    on the current user's role (both routes render the
                    same TicketShow, but role guards forbid cross-use). */}
                <Route path="/tickets/:id" element={<TicketRedirect />} />

                {/* Neutral landing for planning-comment email links:
                    redirects to `/planning-groups/show/:id` (admin/coord)
                    or `/auditor/planning-groups/:id` (auditor) preserving
                    `?tab=comments&ticket=X`. The same PlanningGroupShow
                    component is mounted under both routes. */}
                <Route
                  path="/planning-groups/thread/:id"
                  element={<PlanningCommentRedirect />}
                />

                {/* Planning Groups Routes */}
                <Route path="/planning-groups">
                  <Route index element={<PlanningGroupList />} />
                  <Route path="create" element={<PlanningGroupCreate />} />
                  <Route path="edit/:id" element={<PlanningGroupEdit />} />
                  <Route path="show/:id" element={<PlanningGroupShow />} />
                  <Route path="show/:id/map" element={<PlanningGroupMap />} />
                  <Route
                    path="show/:id/assign-audits"
                    element={<PlanningAssignAudits />}
                  />
                  <Route path="test-custom-map" element={<TestCustomMap />} />
                </Route>

                {/* Audits Routes */}
                <Route path="/audits">
                  <Route index element={<AuditList />} />
                  <Route path=":id" element={<AuditShow />} />
                  <Route path=":id/edit-info" element={<AuditEditInfo />} />
                  <Route path=":id/map" element={<AuditMap />} />
                  <Route path=":id/plots/create" element={<AuditPlotForm />} />
                  <Route path=":id/plots/:plotId/edit" element={<AuditPlotForm />} />
                  <Route path=":id/plots/:plotId/review" element={<AuditPlotReview />} />
                  <Route path=":id/plots/:plotId/geometry" element={<PlotGeometryMap />} />
                  <Route path=":auditId/producer/contacts" element={<ProducerContactsView />} />
                </Route>

                {/* Control Points Routes */}
                <Route path="/control-points">
                  <Route index element={<ControlPointList />} />
                  <Route path="create" element={<ControlPointCreate />} />
                  <Route path=":id" element={<ControlPointShow />} />
                  <Route path="edit/:id" element={<ControlPointEdit />} />
                  <Route path="edit-full/:id" element={<ControlPointEditFull />} />
                </Route>

                {/* Adjacent Crops Routes */}
                <Route path="/adjacent-crops">
                  <Route index element={<AdjacentCropList />} />
                  <Route path="create" element={<AdjacentCropCreate />} />
                  <Route path="edit/:id" element={<AdjacentCropEdit />} />
                  <Route path="map/:farmId" element={<AdjacentCropMap />} />
                  <Route path=":id" element={<AdjacentCropShow />} />
                </Route>

                {/* Auditor section (user_type 4 and 5) */}
                <Route element={<AuditorRoute />}>
                  <Route path="/auditor">
                    <Route index element={<Navigate to="home" replace />} />
                    <Route path="home" element={<AuditorHome />} />
                    <Route
                      path="planning-groups"
                      element={<AuditorPlanningGroupsList />}
                    />
                    {/* Reuse the admin planning detail — same component,
                        admin-only actions inside it are role-gated. */}
                    <Route
                      path="planning-groups/:id"
                      element={<PlanningGroupShow />}
                    />
                    {/* Support ticketing — accessible to auditors via /auditor/incidences */}
                    <Route path="incidences" element={<TicketsList />} />
                    <Route path="incidences/:id" element={<TicketShow />} />
                    {/* Bulk lab-code assignment for samples pending a code.
                        Filters by has_lab_code=false server-side; ESA
                        scope is applied automatically. Available to both
                        auditor and coord since both can assign codes. */}
                    <Route
                      path="samples/pending"
                      element={<AuditorSamplesPending />}
                    />
                    {/* Coordinator-only: assign audits to team members. */}
                    <Route element={<CoordinatorRoute />}>
                      <Route path="assign" element={<CoordinatorAssign />} />
                      <Route path="auditors" element={<CoordinatorAuditors />} />
                    </Route>
                    <Route path="profile" element={<AuditorProfile />} />
                    <Route
                      path="profile/password"
                      element={<AuditorChangePassword />}
                    />
                  </Route>
                </Route>

                {/* Admin only routes */}
                <Route element={<AdminRoute />}>
                  <Route path="/producers">
                    <Route index element={<ProducerList />} />
                    <Route path="create" element={<ProducerCreate />} />
                    <Route path="edit/:id" element={<ProducerEdit />} />
                    <Route path="contacts/import" element={<ContactImportPage />} />
                    <Route path="show/:id" element={<ProducerShow />} />
                    <Route path=":id/farms/create" element={<FarmCreate />} />
                    <Route path=":id/farms/edit/:farmId" element={<FarmEdit />} />
                  </Route>
                  <Route path="/farms">
                    <Route index element={<FarmList />} />
                    <Route path="show/:id" element={<FarmShow />} />
                    <Route path="map/:id" element={<FarmMap />} />
                    <Route
                      path=":farmId/plot-reals/:id/edit"
                      element={<PlotRealEdit />}
                    />
                  </Route>
                  <Route path="/incidences">
                    <Route index element={<IncidenceList />} />
                    <Route path="create" element={<IncidenceCreate />} />
                    <Route path=":id" element={<IncidenceShow />} />
                    <Route path="edit/:id" element={<IncidenceEdit />} />
                    <Route path="regularized-surface-report" element={<RegularizedSurfaceReport />} />
                  </Route>
                  <Route path="/farm-production">
                    <Route index element={<FarmProductionList />} />
                    <Route path="import" element={<FarmProductionImport />} />
                  </Route>
                  {/* Tickets — dentro de AdminRoute (user_type 1 y 2)
                       pero FUERA de CyberAdminRoute. El listado y el
                       detalle los comparten Cyber Admin y ESA Admin
                       (Eurosemillas); el backend filtra vía
                       `visible_tickets_qs` para que ESA solo vea los
                       suyos + los de su portfolio country. */}
                  <Route path="/admin/tickets" element={<TicketsList />} />
                  <Route path="/admin/tickets/:id" element={<TicketShow />} />
                  {/* Mi Perfil (admin) — reutiliza los mismos
                       componentes que la vista auditor. El servicio
                       `/api/auth/me/` que consumen es agnóstico al
                       rol; los componentes ya son role-aware para el
                       botón de cambio de contraseña y la vuelta al
                       perfil. Rutas separadas para respetar el guard
                       AdminRoute y evitar que el auditor caiga aquí. */}
                  <Route path="/admin/profile" element={<AuditorProfile />} />
                  <Route
                    path="/admin/profile/password"
                    element={<AuditorChangePassword />}
                  />
                  {/* Onboarding — manual del puesto. Admin-only. El
                       layout monta un sub-sider propio con los
                       capítulos; el contenido vive en MD/JSON bajo
                       `src/data/onboarding/`. */}
                  <Route path="/onboarding" element={<OnboardingLayout />}>
                    <Route index element={<OnboardingHome />} />
                    <Route path="capitulos/:slug" element={<OnboardingChapterPage />} />
                    <Route path="documentos" element={<OnboardingDocumentsPage />} />
                    <Route path="videos" element={<OnboardingVideosPage />} />
                    <Route path="faq" element={<OnboardingFAQPage />} />
                    {/* Gestión de assets — admin-only. Vive bajo
                         /onboarding para heredar el sub-sider del manual
                         (aparece como "Gestión de assets" en el índice
                         lateral, solo visible para user_type 1/2). El
                         guard AdminRoute externo ya restringe el acceso
                         a la sección entera. */}
                    <Route
                      path="admin/assets"
                      element={<OnboardingAssetsAdminList />}
                    />
                  </Route>
                  {/* /admin/* — Cyber Admin only. Un ESA Admin
                       (incluido el perfil Eurosemillas scoped por
                       portfolio) pasa el guard AdminRoute pero no
                       este subguard: el backoffice global expondría
                       datos fuera de su país. Refactor de arquitectura
                       consciente en Fase 2 del feature Eurosemillas. */}
                  <Route element={<CyberAdminRoute />}>
                    <Route path="/admin/samples">
                      <Route index element={<SampleAdminList />} />
                    </Route>
                    <Route path="/admin/audit-companies">
                      <Route index element={<AuditCompanyAdminList />} />
                      <Route path=":id" element={<AuditCompanyAdminShow />} />
                    </Route>
                    <Route path="/admin/auditor-assignments">
                      <Route index element={<AuditorAssignmentAdminList />} />
                    </Route>
                    <Route path="/admin/users">
                      <Route index element={<AdminUsersList />} />
                    </Route>
                    {/* /admin/tickets movido fuera del CyberAdminRoute
                        — accesible también a ESA. Ver comentario en la
                        ruta que sí lo define. */}
                    <Route path="/admin/ticket-categories">
                      <Route index element={<TicketCategoryAdminList />} />
                    </Route>
                    <Route path="/admin/business-positions">
                      <Route index element={<BusinessPositionsList />} />
                    </Route>
                    <Route path="/admin/contacts/duplicates" element={<AdminContactDuplicatesView />} />
                    <Route path="/admin/patterns">
                      <Route index element={<PatternsList />} />
                    </Route>
                    <Route path="/admin/decrease-reasons">
                      <Route index element={<DecreaseReasonsList />} />
                    </Route>
                    <Route path="/admin/suspicious-crop-reasons">
                      <Route index element={<SuspiciousCropReasonsList />} />
                    </Route>
                    <Route path="/admin/varieties">
                      <Route index element={<VarietiesList />} />
                    </Route>
                    <Route path="/admin/variety-confirmations">
                      <Route index element={<VarietyConfirmationsList />} />
                    </Route>
                    <Route path="/admin/crops">
                      <Route index element={<CropsList />} />
                      <Route path="legacy" element={<CropsLegacyMigrationList />} />
                    </Route>
                    <Route path="/admin/contact-proposals">
                      <Route index element={<ContactProposalsList />} />
                    </Route>
                    <Route path="/admin/seasons">
                      <Route index element={<SeasonsAdminList />} />
                    </Route>
                  </Route>
                </Route>

                {/* Sentry debug route — test error tracking */}
                <Route path="/sentry-debug" element={<SentryDebugPage />} />
              </Route>

              {/* Authentication Routes - Redirect to home if already authenticated */}
              <Route
                element={
                  <Authenticated 
                    key="auth-pages" 
                    fallback={<Outlet />}
                    loading={<LoadingComponent />}
                  >
                    <Navigate to="/" replace />
                  </Authenticated>
                }
              >
                {/* Login Route */}
                <Route path="/login" element={<Login />} />
              </Route>

              {/* Catch-all Route - Redirect to home */}
              <Route path="*" element={<CatchAllNavigate to="/" />} />
            </Routes>

            {/* Refine Utilities */}
            <RefineKbar />
            <UnsavedChangesNotifier />
            <DocumentTitleHandler />
          </Refine>
            </RefineKbarProvider>
          </AntdApp>
        </ConfigProvider>
      </ColorModeContextProvider>
      </Sentry.ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
